import { useState, useCallback, useEffect, useRef } from 'react';

export const DEFAULT_AVITO_APPLICATION_STATE = 'new';
export const ALL_AVITO_APPLICATION_STATES = '__all__';

export interface ResponsesFiltersState {
  searchText: string;
  showOnlyNew: boolean;
  ageFrom: string;
  ageTo: string;
  includeWithoutAge: boolean;
  areaIds: string[];
  relocation: string;
  experience: string[];
  educationLevel: string[];
  gender: string;
  salaryFrom: string;
  salaryTo: string;
  salaryCurrency: string;
  orderBy: string;
  language: string[];
  citizenship: string[];
  driverLicenseTypes: string[];
  hasVehicle: boolean;
  collection: string;
  desiredPosition: string;
  currentPosition: string;
  avitoApplicationState: string;
  avitoApplicationViewed: '' | 'true' | 'false';
  avitoEducationLevel: string[];
  avitoSchedule: string[];
  avitoBusinessTrip: string[];
  avitoRelocation: string[];
  avitoSpecialization: string[];
  avitoExperienceMin: string;
  avitoExperienceMax: string;
  avitoDriverLicence: string;
  avitoDriverLicenceCategory: string[];
  avitoUpdatedFrom: string;
  avitoUpdatedTo: string;
}

export const defaultResponsesFiltersState: ResponsesFiltersState = {
  searchText: '',
  showOnlyNew: true,
  ageFrom: '',
  ageTo: '',
  includeWithoutAge: false,
  areaIds: [],
  relocation: '',
  experience: [],
  educationLevel: [],
  gender: '',
  salaryFrom: '',
  salaryTo: '',
  salaryCurrency: 'RUR',
  orderBy: 'updated_at',
  language: [],
  citizenship: [],
  driverLicenseTypes: [],
  hasVehicle: false,
  collection: 'response',
  desiredPosition: '',
  currentPosition: '',
  avitoApplicationState: DEFAULT_AVITO_APPLICATION_STATE,
  avitoApplicationViewed: '',
  avitoEducationLevel: [],
  avitoSchedule: [],
  avitoBusinessTrip: [],
  avitoRelocation: [],
  avitoSpecialization: [],
  avitoExperienceMin: '',
  avitoExperienceMax: '',
  avitoDriverLicence: '',
  avitoDriverLicenceCategory: [],
  avitoUpdatedFrom: '',
  avitoUpdatedTo: '',
};

export interface UseResponsesFiltersReturn {
  filters: ResponsesFiltersState;
  setField: <K extends keyof ResponsesFiltersState>(field: K, value: ResponsesFiltersState[K]) => void;
  updateFilters: (updates: Partial<ResponsesFiltersState>) => void;
  resetFilters: () => void;
  hasActiveFilters: boolean;
  getApiFilters: () => {
    search_text?: string;
    show_only_new_responses?: boolean;
    age_from?: string;
    age_to?: string;
    only_with_age?: boolean;
    area?: string[];
    relocation?: string;
    experience?: string[];
    education_level?: string[];
    gender?: string;
    salary_from?: string;
    salary_to?: string;
    currency?: string;
    order_by?: string;
    language?: string[];
    citizenship?: string[];
    driver_license_types?: string[];
    has_vehicle?: boolean;
    collection?: string;
    desired_position?: string;
    current_position?: string;
  };
}

const RESPONSES_STATE_KEY_PREFIX = 'responsesState_v';

function getStorageKey(vacancyId: number | null): string | null {
  return vacancyId != null ? `${RESPONSES_STATE_KEY_PREFIX}${vacancyId}` : null;
}

function migrateFilters(filters: any): Partial<ResponsesFiltersState> {
  const migrated = { ...filters };
  
  if (typeof migrated.educationLevel === 'string') {
    migrated.educationLevel = migrated.educationLevel ? [migrated.educationLevel] : [];
  }
  // Множественный выбор образования разрешён
  if (!Array.isArray(migrated.language)) {
    migrated.language = [];
  }
  if (!Array.isArray(migrated.citizenship)) {
    migrated.citizenship = [];
  }
  if (!Array.isArray(migrated.driverLicenseTypes)) {
    migrated.driverLicenseTypes = [];
  }
  if (!Array.isArray(migrated.areaIds)) {
    migrated.areaIds = [];
  }
  if (!Array.isArray(migrated.experience)) {
    migrated.experience = [];
  }
  [
    'avitoEducationLevel',
    'avitoSchedule',
    'avitoBusinessTrip',
    'avitoRelocation',
    'avitoSpecialization',
    'avitoDriverLicenceCategory',
  ].forEach((field) => {
    if (!Array.isArray(migrated[field])) {
      migrated[field] = [];
    }
  });
  delete migrated.avitoDrivingExperience;
  delete migrated.avitoOwnTransport;
  delete migrated.avitoMedicalBook;
  if (migrated.avitoApplicationState === '' || migrated.avitoApplicationState == null) {
    migrated.avitoApplicationState = DEFAULT_AVITO_APPLICATION_STATE;
  }
  
  return migrated;
}

function loadFromStorage(key: string): Partial<ResponsesFiltersState> | null {
  try {
    const saved = localStorage.getItem(key);
    if (saved) {
      const parsed = JSON.parse(saved);
      const filters = parsed?.filters ?? parsed ?? null;
      return filters ? migrateFilters(filters) : null;
    }
  } catch (e) {
    console.error('Error parsing responses state:', e);
  }
  return null;
}

function saveToStorage(key: string, filters: ResponsesFiltersState) {
  try {
    localStorage.setItem(key, JSON.stringify({ filters, timestamp: Date.now() }));
  } catch (e) {
    console.error('Error saving responses state:', e);
  }
}

export function useResponsesFilters(
  defaultFilters: Partial<ResponsesFiltersState> = {},
  vacancyId: number | null = null,
  vacancyDefaultsKey: string = ''
): UseResponsesFiltersReturn {
  const storageKey = getStorageKey(vacancyId);
  const defaultFiltersRef = useRef({ ...defaultResponsesFiltersState, ...defaultFilters });
  defaultFiltersRef.current = { ...defaultResponsesFiltersState, ...defaultFilters };

  const [filters, setFiltersInternal] = useState<ResponsesFiltersState>(() => {
    const defaults = { ...defaultResponsesFiltersState, ...defaultFilters };
    if (!storageKey) return defaults;
    const saved = loadFromStorage(storageKey);
    if (saved) {
      const merged = { ...defaults, ...saved };
      if (defaults.searchText) merged.searchText = defaults.searchText;
      return merged;
    }
    return defaults;
  });

  useEffect(() => {
    if (vacancyId == null) return;
    const key = getStorageKey(vacancyId);
    if (!key) return;
    const defaults = defaultFiltersRef.current;
    const saved = loadFromStorage(key);
    const next = saved ? { ...defaults, ...saved } : defaults;
    if (defaults.searchText) next.searchText = defaults.searchText;
    setFiltersInternal(next);
  }, [vacancyId, vacancyDefaultsKey]);

  const persistFilters = useCallback(
    (next: ResponsesFiltersState) => {
      if (storageKey) saveToStorage(storageKey, next);
    },
    [storageKey]
  );

  const setField = useCallback(
    <K extends keyof ResponsesFiltersState>(field: K, value: ResponsesFiltersState[K]) => {
      setFiltersInternal((prev) => {
        const next = { ...prev, [field]: value };
        persistFilters(next);
        return next;
      });
    },
    [persistFilters]
  );

  const updateFilters = useCallback(
    (updates: Partial<ResponsesFiltersState>) => {
      setFiltersInternal((prev) => {
        const next = { ...prev, ...updates };
        persistFilters(next);
        return next;
      });
    },
    [persistFilters]
  );

  const resetFilters = useCallback(() => {
    const defaults = defaultFiltersRef.current;
    setFiltersInternal(() => {
      const next = { ...defaults };
      if (storageKey) saveToStorage(storageKey, next);
      return next;
    });
  }, [storageKey]);

  const hasActiveFilters =
    filters.searchText !== '' ||
    filters.ageFrom !== '' ||
    filters.ageTo !== '' ||
    filters.areaIds.length > 0 ||
    filters.relocation !== '' ||
    filters.experience.length > 0 ||
    filters.educationLevel.length > 0 ||
    filters.gender !== '' ||
    filters.salaryFrom !== '' ||
    filters.salaryTo !== '' ||
    filters.language.length > 0 ||
    filters.citizenship.length > 0 ||
    filters.driverLicenseTypes.length > 0 ||
    filters.hasVehicle ||
    filters.desiredPosition !== '' ||
    filters.currentPosition !== '' ||
    (filters.avitoApplicationState !== '' && filters.avitoApplicationState !== DEFAULT_AVITO_APPLICATION_STATE) ||
    filters.avitoApplicationViewed !== '' ||
    filters.avitoEducationLevel.length > 0 ||
    filters.avitoSchedule.length > 0 ||
    filters.avitoBusinessTrip.length > 0 ||
    filters.avitoRelocation.length > 0 ||
    filters.avitoSpecialization.length > 0 ||
    filters.avitoExperienceMin !== '' ||
    filters.avitoExperienceMax !== '' ||
    filters.avitoDriverLicence !== '' ||
    filters.avitoDriverLicenceCategory.length > 0 ||
    filters.avitoUpdatedFrom !== '' ||
    filters.avitoUpdatedTo !== '';

  const getApiFilters = useCallback(() => {
    const apiFilters: ReturnType<UseResponsesFiltersReturn['getApiFilters']> = {};
    if (filters.showOnlyNew) apiFilters.show_only_new_responses = true;
    if (filters.searchText) apiFilters.search_text = filters.searchText;
    if (filters.ageFrom) apiFilters.age_from = filters.ageFrom;
    if (filters.ageTo) apiFilters.age_to = filters.ageTo;
    if (!filters.includeWithoutAge && (filters.ageFrom || filters.ageTo)) apiFilters.only_with_age = true;
    if (filters.areaIds.length > 0) apiFilters.area = filters.areaIds;
    if (filters.relocation) apiFilters.relocation = filters.relocation;
    if (filters.experience.length > 0) apiFilters.experience = filters.experience;
    if (filters.educationLevel.length > 0) apiFilters.education_level = filters.educationLevel;
    if (filters.gender) apiFilters.gender = filters.gender;
    if (filters.salaryFrom) apiFilters.salary_from = filters.salaryFrom;
    if (filters.salaryTo) apiFilters.salary_to = filters.salaryTo;
    if (filters.salaryFrom || filters.salaryTo) apiFilters.currency = filters.salaryCurrency;
    if (filters.orderBy) apiFilters.order_by = filters.orderBy;
    if (filters.language.length > 0) apiFilters.language = filters.language;
    if (filters.citizenship.length > 0) apiFilters.citizenship = filters.citizenship;
    if (filters.driverLicenseTypes.length > 0) apiFilters.driver_license_types = filters.driverLicenseTypes;
    if (filters.hasVehicle) apiFilters.has_vehicle = true;
    if (filters.collection && filters.collection !== 'response') apiFilters.collection = filters.collection;
    if (filters.desiredPosition && filters.desiredPosition.trim()) apiFilters.desired_position = filters.desiredPosition.trim();
    if (filters.currentPosition && filters.currentPosition.trim()) apiFilters.current_position = filters.currentPosition.trim();
    return apiFilters;
  }, [filters]);

  return {
    filters,
    setField,
    updateFilters,
    resetFilters,
    hasActiveFilters,
    getApiFilters,
  };
}

export const RESPONSES_EXPERIENCE_OPTIONS = [
  { value: 'noExperience', label: 'Нет опыта' },
  { value: 'between1And3', label: 'От 1 до 3 лет' },
  { value: 'between3And6', label: 'От 3 до 6 лет' },
  { value: 'moreThan6', label: 'Более 6 лет' }
];

export const RESPONSES_ORDER_OPTIONS = [
  { value: 'updated_at', label: 'По дате обновления' },
  { value: 'created_at', label: 'По дате создания' },
  { value: 'relevance', label: 'По релевантности HH' }
];

export const EDUCATION_LEVEL_OPTIONS = [
  { value: 'secondary', label: 'Среднее' },
  { value: 'special_secondary', label: 'Среднее специальное' },
  { value: 'unfinished_higher', label: 'Неоконченное высшее' },
  { value: 'higher', label: 'Высшее' },
  { value: 'bachelor', label: 'Бакалавр' },
  { value: 'master', label: 'Магистр' },
  { value: 'candidate', label: 'Кандидат наук' },
  { value: 'doctor', label: 'Доктор наук' }
];

export const RELOCATION_OPTIONS = [
  { value: 'living_or_relocation', label: 'Живут в указанном регионе или готовы переехать в него' },
  { value: 'living', label: 'Живут в указанном регионе' },
  { value: 'living_but_relocation', label: 'Живут в указанном регионе и готовы к переезду куда-либо' },
  { value: 'relocation', label: 'Не живут в указанном регионе, но готовы переехать в него' }
];

export const DRIVER_LICENSE_OPTIONS = [
  { value: 'A', label: 'A' },
  { value: 'B', label: 'B' },
  { value: 'C', label: 'C' },
  { value: 'D', label: 'D' },
  { value: 'E', label: 'E' },
  { value: 'BE', label: 'BE' },
  { value: 'CE', label: 'CE' },
  { value: 'DE', label: 'DE' },
  { value: 'TM', label: 'TM' },
  { value: 'TB', label: 'TB' }
];

export const CURRENCY_OPTIONS = [
  { value: 'RUR', label: '₽' },
  { value: 'USD', label: '$' },
  { value: 'EUR', label: '€' },
  { value: 'BYR', label: 'Br' },
  { value: 'KZT', label: '₸' },
];

export const GENDER_OPTIONS = [
  { value: 'male', label: 'Мужской' },
  { value: 'female', label: 'Женский' }
];

export const EMPLOYER_STATES_OPTIONS = [
  { value: 'response', label: 'Отклик' },
  { value: 'vacancy_visitors', label: 'Посмотревшие вакансию' },
  { value: 'consider', label: 'Подумать' },
  { value: 'phone_interview', label: 'Первичный контакт' },
  { value: 'assessment', label: 'Тестирование' },
  { value: 'interview', label: 'Собеседование' },
  { value: 'offer', label: 'Предложение о работе' },
  { value: 'hired', label: 'Выход на работу' },
  { value: 'discard_by_employer', label: 'Не подходит' },
  { value: 'discard_by_applicant', label: 'Отказ: кандидат отказался' },
  { value: 'discard_no_interaction', label: 'Отказ: не выходит на связь' },
  { value: 'discard_vacancy_closed', label: 'Отказ: вакансия закрыта' },
  { value: 'discard_to_other_vacancy', label: 'Отказ: перевод на другую вакансию' },
  { value: 'relevant_responses', label: 'Подходящие отклики' },
  { value: 'phone_calls', label: 'Звонки' },
  { value: 'by_location', label: 'Соискатели рядом' },
];
